import { queryOptions, useQuery } from "@tanstack/react-query";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";

import { type DashboardService } from "../DashboardService";

import { dashboardQueryKeys } from "./dashboardQueryKeys";

/**
 * Query options for a single dashboard fetched by id. Extracted from the hook
 * so node unit tests can drive it through a bare `QueryObserver` (the "unit"
 * vitest project has no DOM to mount the hook in).
 *
 * The query is disabled until both an org and a non-empty dashboard id are
 * known: `dashboardId` derives from the `/metrics/:dashboardId?` route path
 * param, so it can be `null` (segment absent) or `""` (defensive: callers
 * normalize blank segments to `null`, but the contract still admits it) — an
 * empty id must never fire a request, since it would hit the malformed
 * `v1/metrics/dashboards/id/` URL and cache the response under the same
 * sentinel key the disabled state uses.
 */
export function dashboardQueryOptions(
  dashboardService: DashboardService,
  dashboardId: string | null,
  orgId: string | undefined,
) {
  return queryOptions({
    queryKey: dashboardQueryKeys.id(dashboardId ?? "", orgId ?? ""),
    enabled: Boolean(dashboardId) && orgId !== undefined,
    queryFn: async ({ signal }) => {
      // Belt and braces with `enabled`: even if a future edit re-enables the
      // query for a falsy id, never issue the malformed request.
      if (orgId === undefined || dashboardId === null || dashboardId === "") {
        return Promise.reject(
          new Error("CurrentOrganization or dashboardId is not set"),
        );
      }

      return dashboardService.getDashboardById(dashboardId, orgId, {
        abortSignal: signal,
      });
    },
  });
}

/**
 * The dashboard record for `dashboardId` in the current org, or a disabled
 * query when the id is `null`/empty or the org is not yet known.
 */
export function useDashboard(dashboardId: string | null) {
  const { dashboardService } = useDomainServices();
  const { currentOrganization } = useAuth();

  return useQuery(
    dashboardQueryOptions(
      dashboardService,
      dashboardId,
      currentOrganization?.org_id,
    ),
  );
}
